/**
 * The first-time and return-visit alert editor. `Dialog` IS the sheet: a
 * bottom sheet under 768px, a centered dialog at 768px and up, from one
 * component.
 *
 * One deliberate deviation from `16-team-follow-control.md`: the brief says
 * the first-time editor on a full card "expands in place under the header
 * instead of opening a sheet," but this issue's D5 says the first-time
 * follow opens the auto-editor SHEET, and the same brief's web platform
 * delta row calls for a sheet under 768px and a centered dialog above.
 * `Dialog` is both of those in one, so this opens `Dialog` and builds no
 * in-place expansion.
 *
 * Each switch writes the moment it is flipped, so there is no dirty state
 * and `Done` closes rather than submits.
 */
import { Button, Dialog, Switch } from "@sportsmet/ui";
import type { ReactNode } from "react";

import {
  EDITOR_DONE_LABEL,
  editorBody,
  editorTitle,
  type typeAlertKindKey,
  type typeAlertSwitchRow,
} from "./teamFollowLogic";

export const TEAM_ALERTS_SHEET = "team-alerts";

export type typeTeamAlertsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamName: string;
  isFirstFollow: boolean;
  alertRows: readonly typeAlertSwitchRow[];
  onSetAlert: (alertKindKey: typeAlertKindKey, isEnabled: boolean) => void;
};

export const TeamAlertsDialog = (
  props: typeTeamAlertsDialogProps,
): ReactNode => (
  <Dialog
    open={props.open}
    onOpenChange={props.onOpenChange}
    title={editorTitle(props.teamName, props.isFirstFollow)}
    description={editorBody(props.isFirstFollow) ?? undefined}
    footer={
      <Button
        variant="primary"
        onClick={() => {
          props.onOpenChange(false);
        }}
      >
        {EDITOR_DONE_LABEL}
      </Button>
    }
  >
    <div data-testid="team-alerts-rows" className="flex flex-col">
      {props.alertRows.map((row) => (
        <Switch
          key={row.alertKindKey}
          label={row.label}
          checked={row.isEnabled}
          onCheckedChange={(next) => {
            props.onSetAlert(row.alertKindKey, next);
          }}
        />
      ))}
    </div>
  </Dialog>
);
